import React, { useState, useEffect } from 'react'
import './styles.css'

import { useSearchParams } from 'react-router-dom'

import { Button, Collapse, Snackbar, TextField } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { styled } from '@mui/material/styles'

import Copyright from 'src/common/components/Copyright'
import AuthService from '../../services/auth.service'
import settingsService from 'src/services/settings.service'
import { OktaLogo } from 'src/common/logo/okta'

const LoginInput = styled(TextField)(() => ({
  border: '1px solid #E7E7EE',
  padding: '10px 16px',
  fontSize: '16px',
  '& .MuiFormLabel-root': {
    fontSize: '16px',
    left: '16px',
    top: '4px'
  },
  '& .MuiFormLabel-root.MuiFormLabel-filled': {
    top: '10px',
    fontSize: '14px'
  },
  '& .MuiFormLabel-root.Mui-focused': {
    fontSize: '14px',
    color: '#76767F',
    top: '10px'
  },
  '& .MuiInput-root:before': {
    display: 'none'
  },
  '& .MuiInput-root:after': {
    display: 'none'
  },

  '@media (max-width: 480px)': {
    padding: '8px 10px',

    '& .MuiFormLabel-root': {
      top: '0px'
    }
  }
}))

const LoginPage = () => {
  const [email, setEmail] = useState('')
  const [oktaEmail, setOktaEmail] = useState('')
  const [dataSent, setDataSent] = useState(false)
  const [loading, setLoading] = useState(false)
  const [ssoLoading, setSsoLoading] = useState(false)
  const [error, setError] = useState(null)
  const [errors, setErrors] = useState({})
  const [searchParams, setSearchParams] = useSearchParams()

  const [showOktaMailInput, setShowOktaMailInput] = useState(false)

  useEffect(() => {
    if (searchParams.has('error')) {
      setErrors({ ...errors, tokenError: searchParams.get('error') })
      setTimeout(() => {
        setErrors({ ...errors, tokenError: null })
      }, 5000)
      searchParams.delete('error')
      setSearchParams(searchParams)
    }
  }, [])

  const validateForm = () => {
    let errors = {}

    if (!email) {
      errors.email = 'Email is required'
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = 'Email is invalid'
    }

    setErrors(errors)
    return Object.keys(errors).length === 0
  }
  const validateOktaForm = () => {
    let errors = {}

    if (!oktaEmail) {
      errors.oktaEmail = 'Email is required'
    } else if (!/\S+@\S+\.\S+/.test(oktaEmail)) {
      errors.oktaEmail = 'Email is invalid'
    }

    setErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleLoginGoogle = () => {
    settingsService.getGoogleAuthSsoUrl().then(resp => {
      window.location = resp.data
    })
  }
  const handleLoginOutlook = () => {
    settingsService.getOutlookAuthSsoUrl().then(resp => {
      window.location = resp.data
    })
  }
  const handleLoginOkta = () => {
    setShowOktaMailInput(!showOktaMailInput)
    setErrors({})
    setOktaEmail('')
  }

  const handleOktaLogin = e => {
    e.preventDefault()

    if (validateOktaForm()) {
      setSsoLoading(true)
      settingsService
        .getOktaAuthSsoUrl(oktaEmail)
        .then(resp => {
          localStorage.setItem('oktaSSOPrevPage', 'login')
          localStorage.setItem('prevEmail', oktaEmail)
          window.location = resp.data
        })
        .catch(err => {
          setSsoLoading(false)
          console.error(err)
          setErrors({ oktaEmail: err?.response?.data.description || 'Something went wrong' })
        })
    }
  }

  const handleLogin = e => {
    e.preventDefault()

    if (validateForm()) {
      setLoading(true)
      AuthService.authLink(email).then(
        res => {
          if (res?.error) {
            setError('Access Denied')
          } else {
            if (res.type == 'SAML') {
              window.location = res.link
            } else {
              setDataSent(true)
            }
          }
          setLoading(false)
        },
        error => {
          setLoading(false)
          setError(error?.response?.data.description)
        }
      )
    }
  }
  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return
    }

    setError(false)
  }
  return (
    <div className='login-page column'>
      <div className='logo'>
        <img src='images/njsig-logo-1.png' style={{ width: 200 }} alt='' />
      </div>
      <div className='login-form-container mob-login-form-container column'>
        <form className='column' onSubmit={handleLogin}>
          {dataSent ? (
            <>
              <div className='column column-center'>
                <svg
                  style={{ marginBottom: '24px' }}
                  width='112'
                  height='112'
                  viewBox='0 0 112 112'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    opacity='0.16'
                    fillRule='evenodd'
                    clipRule='evenodd'
                    d='M56 112C86.9279 112 112 86.9279 112 56C112 25.0721 86.9279 0 56 0C25.0721 0 0 25.0721 0 56C0 86.9279 25.0721 112 56 112Z'
                    fill='#00BC62'
                  />
                  <g filter='url(#filter0_d_360_35055)'>
                    <path
                      fillRule='evenodd'
                      clipRule='evenodd'
                      d='M79.7744 38.82C78.648 37.4334 76.6041 37.2221 75.2205 38.351L47.5584 60.8834L36.3493 54.4157C35.1674 53.7316 33.6784 53.8707 32.6407 54.753C31.2812 55.9098 31.1129 57.9619 32.2652 59.3263L39.3125 67.6496L39.2977 67.6626L44.7821 74.1174L44.9929 74.3435C45.6218 74.9478 46.432 75.25 47.2403 75.25C48.0893 75.25 48.9346 74.9182 49.5691 74.2638L79.5895 43.1207C80.7289 41.9417 80.8084 40.0936 79.7744 38.82Z'
                      fill='url(#paint0_linear_360_35055)'
                    />
                  </g>
                  <defs>
                    <filter
                      id='filter0_d_360_35055'
                      x='31.5'
                      y='37.625'
                      width='49'
                      height='39.125'
                      filterUnits='userSpaceOnUse'
                      colorInterpolationFilters='sRGB'
                    >
                      <feFlood floodOpacity='0' result='BackgroundImageFix' />
                      <feColorMatrix
                        in='SourceAlpha'
                        type='matrix'
                        values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'
                        result='hardAlpha'
                      />
                      <feOffset dy='1.5' />
                      <feComposite in2='hardAlpha' operator='out' />
                      <feColorMatrix
                        type='matrix'
                        values='0 0 0 0 0.0032804 0 0 0 0 0.61672 0 0 0 0 0.347725 0 0 0 1 0'
                      />
                      <feBlend mode='normal' in2='BackgroundImageFix' result='effect1_dropShadow_360_35055' />
                      <feBlend mode='normal' in='SourceGraphic' in2='effect1_dropShadow_360_35055' result='shape' />
                    </filter>
                    <linearGradient
                      id='paint0_linear_360_35055'
                      x1='74.1597'
                      y1='5.09505'
                      x2='38.0323'
                      y2='9.27992'
                      gradientUnits='userSpaceOnUse'
                    >
                      <stop stopColor='#00BA97' />
                      <stop offset='1' stopColor='#00BC62' />
                    </linearGradient>
                  </defs>
                </svg>
                <p className='login-title'>Link sent!</p>
              </div>
              <p style={{ textAlign: 'center', marginBottom: '0' }} className='reset-pass-description'>
                We've sent magic link to login to <br />
                <span style={{ fontWeight: '600' }}>{email}</span>
              </p>
              <p
                style={{
                  textAlign: 'center',
                  marginBottom: '64px'
                }}
                className='reset-pass-description'
              >
                Check your inbox (and spam folder) for an email from us —if you don't receive an email, try another
                email.
              </p>
            </>
          ) : (
            <>
              <p className='login-title'>Welcome to NJSIG Security Awareness Platform,</p>
              <p className='login-title' style={{ marginBottom: 30 }}>
                Log in to continue
              </p>
              {/* <Link className='login-link' to='/forgot-pass'>
          Forgot password?
        </Link> */}

              <LoginInput
                sx={{
                  color: (error || errors.email) && '#B40020',
                  backgroundColor: (error || errors.email) && '#FAF0F2',
                  '& input::-webkit-autofill': {
                    WebkitTextFillColor: '#FAF0F2'
                  },
                  borderRadius: '20px',
                  mb: '-1px'
                }}
                label='Your Email'
                variant='standard'
                value={email}
                validations={[]}
                onChange={e => setEmail(e.target.value)}
                error={error || errors.email}
              />
              {(error || errors.email) && <p className='error-text'>The entered email address is incorrect</p>}

              <LoadingButton
                type='submit'
                loading={loading}
                onClick={handleLogin}
                sx={{
                  mt: '30px',
                  borderRadius: '14px',
                  textTransform: 'none',
                  height: '60px',
                  backgroundColor: '#6123FF'
                }}
                variant={'contained'}
              >
                Continue
              </LoadingButton>
            </>
          )}

          <div className='row row-center' style={{ margin: '30px 0px 24px' }}>
            <div style={{ flex: 1, height: 1, background: '#E7E7EE' }}></div>
            <p
              style={{
                color: '#76767F',
                fontSize: '14px',
                margin: '0px 8px'
              }}
            >
              Or log in with
            </p>
            <div style={{ flex: 1, height: 1, background: '#E7E7EE' }}></div>
          </div>
        </form>
        <div className='row'>
          <Button
            onClick={handleLoginGoogle}
            variant={'outlined'}
            sx={{
              padding: '10px 0px',
              fontFamily: 'Plus Jakarta Sans',
              textTransform: 'none',
              borderRadius: '8px',
              borderColor: '#D0D0D8',
              color: '#1B1B26',
              flex: 1,
              fontWeight: 600
            }}
          >
            <svg width='23' height='22' viewBox='0 0 23 22' fill='none' xmlns='http://www.w3.org/2000/svg'>
              <path
                d='M22.2669 11.24C22.2669 10.5158 22.2027 9.82835 22.0927 9.15918H11.7344V13.2933H17.6652C17.3994 14.65 16.6202 15.7958 15.4652 16.575V19.325H19.0035C21.0752 17.4092 22.2669 14.5858 22.2669 11.24Z'
                fill='#4285F4'
              />
              <path
                d='M11.7335 22.0063C14.7035 22.0063 17.1877 21.0163 19.0027 19.3388L15.4643 16.5888C14.4743 17.2488 13.2185 17.6521 11.7335 17.6521C8.86435 17.6521 6.43518 15.718 5.56435 13.1055H1.91602V15.938C3.72185 19.5313 7.43435 22.0063 11.7335 22.0063Z'
                fill='#34A853'
              />
              <path
                d='M5.56521 13.0963C5.33604 12.4363 5.21688 11.7304 5.21688 10.9971C5.21688 10.2638 5.34521 9.55793 5.56521 8.89793V6.06543H1.91687C1.16521 7.55043 0.734375 9.21876 0.734375 10.9971C0.734375 12.7754 1.16521 14.4438 1.91687 15.9288L5.56521 13.0963Z'
                fill='#FBBC05'
              />
              <path
                d='M11.7335 4.35417C13.356 4.35417 14.8043 4.91334 15.9502 6.00417L19.0852 2.86917C17.1877 1.09083 14.7035 0 11.7335 0C7.43435 0 3.72185 2.475 1.91602 6.06834L5.56435 8.90084C6.43518 6.28834 8.86435 4.35417 11.7335 4.35417Z'
                fill='#EA4335'
              />
            </svg>
          </Button>
          <div style={{ width: 24 }}></div>
          <Button
            onClick={handleLoginOutlook}
            variant={'outlined'}
            sx={{
              padding: '10px 0px',
              fontFamily: 'Plus Jakarta Sans',
              textTransform: 'none',
              borderRadius: '8px',
              borderColor: '#D0D0D8',
              color: '#1B1B26',
              flex: 1,
              fontWeight: 600
            }}
          >
            <svg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'>
              <rect x='10.8633' y='10.8633' width='8.63636' height='8.63636' fill='#FEBA08' />
              <rect x='0.5' y='10.8632' width='8.63636' height='8.63636' fill='#05A6F0' />
              <rect x='10.8633' y='0.5' width='8.63636' height='8.63636' fill='#80BC06' />
              <rect x='0.5' y='0.5' width='8.63636' height='8.63636' fill='#F25325' />
            </svg>
          </Button>
          <div style={{ width: 24 }}></div>
          <Button
            // disabled={true}
            onClick={handleLoginOkta}
            variant={'outlined'}
            sx={{
              padding: '10px 0px',
              fontFamily: 'Plus Jakarta Sans',
              textTransform: 'none',
              borderRadius: '8px',
              borderColor: '#D0D0D8',
              color: '#1B1B26',
              flex: 1,
              fontWeight: 600
            }}
          >
            <div style={{ display: 'flex' }}>
              <OktaLogo />
            </div>
          </Button>
        </div>
        <Collapse in={showOktaMailInput} collapsedSize={0}>
          <form onSubmit={handleOktaLogin}>
            <div className='row row-center' style={{ width: '100%', marginTop: '20px' }}>
              <LoginInput
                sx={{
                  color: (error || errors.oktaEmail) && '#B40020',
                  backgroundColor: (error || errors.oktaEmail) && '#FAF0F2',
                  '& input::-webkit-autofill': {
                    WebkitTextFillColor: '#FAF0F2'
                  },
                  borderRadius: '20px',
                  mr: '20px',
                  flex: 1
                }}
                placeholder='Your Okta Email'
                variant='standard'
                value={oktaEmail}
                validations={[]}
                onChange={e => {
                  setOktaEmail(e.target.value.trim())
                  setErrors({})
                }}
                error={error || !!errors.oktaEmail}
              />
              <LoadingButton
                loading={ssoLoading}
                onClick={handleOktaLogin}
                sx={{
                  borderRadius: '14px',
                  textTransform: 'none',
                  height: '53px',
                  backgroundColor: '#6123FF'
                }}
                variant={'contained'}
              >
                Continue
              </LoadingButton>
            </div>
          </form>
          {(error || errors.oktaEmail) && (
            <p className='error-text'>{errors.oktaEmail || 'The entered email address is incorrect'}</p>
          )}
        </Collapse>

        {errors?.tokenError && (
          <div className='column column-center'>
            <p className='error-text' style={{ marginTop: 20 }}>
              {' '}
              {errors?.tokenError}
            </p>
          </div>
        )}
      </div>
      <div className='spacer'></div>
      <Copyright />
      <Snackbar
        sx={{
          '& .MuiSnackbarContent-root': {
            backgroundColor: '#B40020',
            borderRadius: '8px'
          }
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        open={error}
        autoHideDuration={6000}
        onClose={handleClose}
        message={error}
      />
      <Snackbar
        sx={{
          '& .MuiSnackbarContent-root': {
            backgroundColor: '#B40020',
            borderRadius: '8px'
          }
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        open={!!errors?.tokenError}
        autoHideDuration={6000}
        onClose={handleClose}
        message={errors?.tokenError}
      />
    </div>
  )
}

export default LoginPage
